// Internal constant, not exported
const STORAGE_KEY = 'invirtus_local_storage_20250405';

export interface StorageItem {
  id: string;
  [key: string]: unknown;
}

interface StorageData<T extends StorageItem> {
  active: T[];
  archived: T[];
}

// Internal function, not exported
const getStorageData = <T extends StorageItem>(): StorageData<T> => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return { active: [], archived: [] };
    
    const parsed = JSON.parse(data);
    // Handle migration from old format
    if (Array.isArray(parsed)) {
      return { active: parsed, archived: [] };
    }
    return parsed;
  } catch {
    return { active: [], archived: [] };
  }
};

export const getStoredItems = <T extends StorageItem>(): StorageData<T> => {
  return getStorageData<T>();
};

export const addItem = <T extends StorageItem>(item: T): void => {
  const storage = getStorageData<T>();
  storage.active = [item, ...storage.active];
  localStorage.setItem(STORAGE_KEY, JSON.stringify(storage));
};

export const updateItem = <T extends StorageItem>(item: T): void => {
  const storage = getStorageData<T>();
  storage.active = storage.active.map(r => r.id === item.id ? item : r);
  storage.archived = storage.archived.map(r => r.id === item.id ? item : r);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(storage));
};

export const deleteItem = <T extends StorageItem>(id: string): void => {
  const storage = getStorageData<T>();
  storage.active = storage.active.filter(r => r.id !== id);
  storage.archived = storage.archived.filter(r => r.id !== id);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(storage));
};

export const archiveItem = <T extends StorageItem>(id: string): void => {
  const storage = getStorageData<T>();
  const item = storage.active.find(r => r.id === id);
  if (item) {
    storage.active = storage.active.filter(r => r.id !== id);
    storage.archived = [item, ...storage.archived];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(storage));
  }
};

export const unarchiveItem = <T extends StorageItem>(id: string): void => {
  const storage = getStorageData<T>();
  const item = storage.archived.find(r => r.id === id);
  if (item) {
    storage.archived = storage.archived.filter(r => r.id !== id);
    storage.active = [item, ...storage.active];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(storage));
  }
};

export const clearStorage = (): void => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ active: [], archived: [] }));
};

export const exportAllData = <T extends StorageItem>(): string => {
  const storage = getStoredItems<T>();
  return JSON.stringify(storage, null, 2);
}; 
